import { useState, useEffect, useMemo, useCallback } from 'react';
import api from '@/utils/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  DollarSign, Clock, Calendar, ChevronLeft, ChevronRight, Trash2,
  CheckCircle2, XCircle, AlertTriangle, Waves, Users
} from 'lucide-react';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

export default function AdminPayrollTab({ instructors, onSelectInstructor }) {
  const [selectedId, setSelectedId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [view, setView] = useState('overview');
  const [dateFilter, setDateFilter] = useState({ from: '', to: '' });
  const [showDeleteSession, setShowDeleteSession] = useState(null);
  const [payPeriod, setPayPeriod] = useState(() => localStorage.getItem('payroll_period') || 'monthly');

  // Save pay period preference
  const changePayPeriod = (p) => {
    setPayPeriod(p);
    localStorage.setItem('payroll_period', p);
    // Auto-set date filter to current period
    applyPeriod(p, 0);
  };

  const applyPeriod = (period, offset) => {
    const now = new Date();
    if (period === 'monthly') {
      const m = now.getMonth() + offset;
      const y = now.getFullYear() + Math.floor(m / 12);
      const mo = ((m % 12) + 12) % 12;
      const from = `${y}-${String(mo + 1).padStart(2, '0')}-01`;
      const lastDay = new Date(y, mo + 1, 0).getDate();
      const to = `${y}-${String(mo + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      setDateFilter({ from, to });
      setCalMonth(mo);
      setCalYear(y);
    } else {
      // Bi-weekly: 1st-15th and 16th-end of month
      const month = now.getMonth();
      const year = now.getFullYear();
      const day = now.getDate();
      const isFirstHalf = day <= 15;
      let periodIdx = isFirstHalf ? 0 : 1;
      periodIdx += offset;

      let m = month;
      let y = year;
      while (periodIdx < 0) { m--; if (m < 0) { m = 11; y--; } periodIdx += 2; }
      while (periodIdx > 1) { m++; if (m > 11) { m = 0; y++; } periodIdx -= 2; }

      if (periodIdx === 0) {
        const from = `${y}-${String(m + 1).padStart(2, '0')}-01`;
        const to = `${y}-${String(m + 1).padStart(2, '0')}-15`;
        setDateFilter({ from, to });
      } else {
        const lastDay = new Date(y, m + 1, 0).getDate();
        const from = `${y}-${String(m + 1).padStart(2, '0')}-16`;
        const to = `${y}-${String(m + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
        setDateFilter({ from, to });
      }
      setCalMonth(m);
      setCalYear(y);
    }
  };

  const getPeriodLabel = () => {
    if (!dateFilter.from || !dateFilter.to) return 'All Time';
    const from = new Date(dateFilter.from + 'T12:00:00');
    const to = new Date(dateFilter.to + 'T12:00:00');
    if (payPeriod === 'monthly') {
      return from.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    return `${from.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${to.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  const fetchPayroll = useCallback(async (instId) => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/payroll?instructor_id=${instId}`);
      setData(res.data);
    } catch { toast.error('Failed to load payroll data'); }
    finally { setLoading(false); }
  }, []);

  const selectInstructor = (inst) => {
    setSelectedId(inst.user_id);
    fetchPayroll(inst.user_id);
    if (onSelectInstructor) onSelectInstructor(inst);
  };

  const handleHourAction = async (logId, status) => {
    try {
      await api.put(`/hours/${logId}`, { status });
      toast.success(`Hour ${status}`);
      fetchPayroll(selectedId);
    } catch { toast.error('Failed'); }
  };

  const deleteHour = async (logId) => {
    try {
      await api.delete(`/admin/hours/${logId}`);
      toast.success('Hour removed');
      fetchPayroll(selectedId);
    } catch { toast.error('Failed'); }
  };

  const deleteSession = async (sessionId) => {
    try {
      await api.delete(`/admin/sessions/${sessionId}`);
      toast.success('Session deleted');
      setShowDeleteSession(null);
      fetchPayroll(selectedId);
    } catch { toast.error('Failed to delete'); }
  };

  // Calendar data
  const calendarMap = useMemo(() => {
    if (!data) return {};
    const map = {};
    for (const s of data.sessions) {
      const d = s.local_date || s.date || (s.created_at || '').slice(0, 10);
      if (!map[d]) map[d] = [];
      map[d].push(s);
    }
    return map;
  }, [data]);

  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const firstDay = new Date(calYear, calMonth, 1).getDay();
  const calCells = [];
  for (let i = 0; i < firstDay; i++) calCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) calCells.push(d);

  // Filtered sessions/hours for date range
  const filteredSessions = useMemo(() => {
    if (!data) return [];
    return data.sessions.filter(s => {
      const d = s.local_date || s.date || (s.created_at || '').slice(0, 10);
      if (dateFilter.from && d < dateFilter.from) return false;
      if (dateFilter.to && d > dateFilter.to) return false;
      return true;
    });
  }, [data, dateFilter]);

  const filteredHours = useMemo(() => {
    if (!data) return [];
    return data.hours.filter(h => {
      const d = h.session_date || (h.created_at || '').slice(0, 10);
      if (dateFilter.from && d < dateFilter.from) return false;
      if (dateFilter.to && d > dateFilter.to) return false;
      return true;
    });
  }, [data, dateFilter]);

  const filteredPay = useMemo(() => {
    const approved = filteredHours.filter(h => h.status === 'approved');
    return {
      hours: approved.reduce((s, h) => s + h.hours, 0),
      total: approved.reduce((s, h) => s + h.hours, 0) * (data?.instructor?.pay_rate || 37.5),
    };
  }, [filteredHours, data]);

  const todayStr = new Date().toISOString().slice(0, 10);

  if (!selectedId) {
    return (
      <div data-testid="admin-payroll-tab">
        <h2 className="text-xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Nunito' }}>Payroll</h2>
        <p className="text-sm text-slate-500 mb-4">Select an instructor to view their payroll details</p>
        <div className="grid gap-3">
          {instructors.map(inst => (
            <Card key={inst.user_id} className="rounded-2xl border-slate-100 cursor-pointer hover:shadow-md transition-shadow" onClick={() => selectInstructor(inst)} data-testid={`payroll-select-${inst.user_id}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-[#1E1B4B] flex items-center justify-center text-white font-bold">{(inst.name || inst.email || '?')[0].toUpperCase()}</div>
                <div className="flex-1">
                  <p className="font-bold text-slate-800">{inst.name || inst.email}</p>
                  <p className="text-xs text-slate-500">${inst.pay_rate || 37.5}/hr · {inst.children?.length || 0} students</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (loading || !data) return <div className="flex items-center justify-center p-12"><Waves className="w-10 h-10 text-[#22D3EE] animate-pulse" /></div>;

  return (
    <div data-testid="admin-payroll-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <Button variant="ghost" size="sm" className="text-slate-400 mb-1" onClick={() => { setSelectedId(null); setData(null); }}>← Back to instructors</Button>
          <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Nunito' }}>{data.instructor.name}'s Payroll</h2>
          <p className="text-xs text-slate-500">${data.instructor.pay_rate}/hr</p>
        </div>
        <div className="flex gap-2">
          {['overview', 'sessions', 'hours'].map(v => (
            <button key={v} onClick={() => setView(v)} className={`px-4 py-2 rounded-xl text-sm font-bold capitalize transition-all ${view === v ? 'bg-[#1E1B4B] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid={`payroll-view-${v}`}>{v}</button>
          ))}
        </div>
      </div>

      {/* Summary Cards — show filtered period data when filter active */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <Card className="rounded-2xl border-slate-100"><CardContent className="p-4 text-center">
          <p className="text-2xl font-extrabold text-slate-800">{dateFilter.from ? filteredSessions.length : data.summary.total_sessions}</p>
          <p className="text-xs text-slate-500">{dateFilter.from ? 'Period' : 'Total'} Sessions</p>
        </CardContent></Card>
        <Card className="rounded-2xl border-slate-100"><CardContent className="p-4 text-center">
          <p className="text-2xl font-extrabold text-green-700">{dateFilter.from ? filteredPay.hours.toFixed(1) : data.summary.approved_hours}</p>
          <p className="text-xs text-green-600">{dateFilter.from ? 'Period' : 'Approved'} Hours</p>
        </CardContent></Card>
        <Card className="rounded-2xl border-slate-100"><CardContent className="p-4 text-center">
          <p className="text-2xl font-extrabold text-amber-600">{dateFilter.from ? filteredHours.filter(h => h.status === 'pending').length : data.summary.pending_hours}</p>
          <p className="text-xs text-amber-500">Pending Hours</p>
        </CardContent></Card>
        <Card className="rounded-2xl border-[#22D3EE]/30"><CardContent className="p-4 text-center">
          <p className="text-2xl font-extrabold text-[#0E7490]">${dateFilter.from ? filteredPay.total.toFixed(2) : data.summary.total_pay.toFixed(2)}</p>
          <p className="text-xs text-[#0E7490]">{dateFilter.from ? 'Period' : 'Total'} Pay</p>
        </CardContent></Card>
      </div>

      {/* Pay Period Toggle & Quick Selectors */}
      <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div className="flex items-center gap-1 bg-slate-100 rounded-full p-0.5">
            {['monthly', 'biweekly'].map(p => (
              <button key={p} onClick={() => changePayPeriod(p)} className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${payPeriod === p ? 'bg-[#1E1B4B] text-white' : 'text-slate-500 hover:text-slate-700'}`} data-testid={`period-${p}`}>
                {p === 'monthly' ? 'Monthly' : 'Bi-weekly'}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={() => applyPeriod(payPeriod, -1)} data-testid="period-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm font-bold text-slate-700 min-w-[140px] text-center">{getPeriodLabel()}</span>
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={() => applyPeriod(payPeriod, 1)} data-testid="period-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" className="rounded-full text-xs h-8" onClick={() => applyPeriod(payPeriod, 0)} data-testid="period-current">
            Current {payPeriod === 'monthly' ? 'Month' : 'Period'}
          </Button>
          <Button variant="outline" size="sm" className="rounded-full text-xs h-8" onClick={() => applyPeriod(payPeriod, -1)} data-testid="period-previous">
            Previous {payPeriod === 'monthly' ? 'Month' : 'Period'}
          </Button>
          <Button variant="outline" size="sm" className="rounded-full text-xs h-8" onClick={() => setDateFilter({ from: '', to: '' })} data-testid="period-all">
            All Time
          </Button>
          {(dateFilter.from || dateFilter.to) && (
            <Badge className="rounded-full bg-[#ECFEFF] text-[#0E7490] border-[#22D3EE]/30 text-xs">
              {filteredSessions.length} sessions · {filteredPay.hours.toFixed(1)}hrs · ${filteredPay.total.toFixed(2)}
            </Badge>
          )}
        </div>
        {/* Manual date override */}
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
          <span className="text-xs text-slate-400">Custom:</span>
          <Input type="date" className="rounded-xl w-36 h-8 text-xs" value={dateFilter.from} onChange={e => setDateFilter(f => ({...f, from: e.target.value}))} data-testid="payroll-date-from" />
          <span className="text-xs text-slate-400">to</span>
          <Input type="date" className="rounded-xl w-36 h-8 text-xs" value={dateFilter.to} onChange={e => setDateFilter(f => ({...f, to: e.target.value}))} data-testid="payroll-date-to" />
        </div>
      </div>

      {view === 'overview' && (
        <div>
          {/* Calendar */}
          <Card className="rounded-2xl border-slate-100 mb-6">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <Button variant="ghost" size="icon" onClick={() => { if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); } else setCalMonth(m => m - 1); }}><ChevronLeft className="w-4 h-4" /></Button>
                <h3 className="font-bold text-slate-700" style={{ fontFamily: 'Nunito' }}>{MONTHS[calMonth]} {calYear}</h3>
                <Button variant="ghost" size="icon" onClick={() => { if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); } else setCalMonth(m => m + 1); }}><ChevronRight className="w-4 h-4" /></Button>
              </div>
              <div className="grid grid-cols-7 gap-1">
                {DAYS.map(d => <div key={d} className="text-center text-xs font-bold text-slate-400 py-1">{d}</div>)}
                {calCells.map((day, i) => {
                  if (!day) return <div key={`e-${i}`} />;
                  const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const sessions = calendarMap[dateStr] || [];
                  const isToday = dateStr === todayStr;
                  return (
                    <div key={dateStr} className={`min-h-[60px] rounded-xl p-1 text-xs border transition-colors ${isToday ? 'border-[#22D3EE] bg-[#ECFEFF]' : sessions.length > 0 ? 'border-[#C4F017]/50 bg-[#F7FEE7]' : 'border-slate-100'}`}>
                      <span className={`font-bold ${isToday ? 'text-[#0E7490]' : 'text-slate-600'}`}>{day}</span>
                      <div className="mt-0.5 space-y-0.5">
                        {sessions.slice(0, 2).map(s => (
                          <div key={s.session_id} className="bg-[#1E1B4B] text-white text-[9px] rounded px-1 py-0.5 truncate leading-tight" title={`${s.child_name} - Session #${s.session_number}`}>
                            {s.child_name} #{s.session_number}
                          </div>
                        ))}
                        {sessions.length > 2 && <div className="text-[9px] text-slate-400">+{sessions.length - 2}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Recent activity */}
          <h3 className="font-bold text-slate-700 mb-3" style={{ fontFamily: 'Nunito' }}>Recent Sessions</h3>
          <div className="space-y-2 max-h-[400px] overflow-auto">
            {filteredSessions.slice(0, 20).map(s => {
              const d = s.local_date || s.date || (s.created_at || '').slice(0, 10);
              return (
                <div key={s.session_id} className="flex items-center justify-between bg-slate-50 rounded-xl p-3" data-testid={`payroll-session-${s.session_id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-[#ECFEFF] text-[#0E7490] flex items-center justify-center font-bold text-xs">#{s.session_number}</div>
                    <div>
                      <p className="font-semibold text-slate-700 text-sm">{s.child_name}</p>
                      <p className="text-xs text-slate-400">{new Date(d + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} · {s.status}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={`rounded-full text-xs ${s.status === 'completed' ? 'bg-green-50 text-green-600 border-green-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>{s.status}</Badge>
                    <Button size="sm" variant="ghost" className="text-slate-300 hover:text-red-500 h-7 w-7 p-0" onClick={() => setShowDeleteSession(s)} data-testid={`payroll-delete-${s.session_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === 'sessions' && (
        <div className="space-y-2">
          <p className="text-sm text-slate-500 mb-2">All sessions for {data.instructor.name} — showing who was instructed and session details</p>
          {filteredSessions.map(s => {
            const d = s.local_date || s.date || (s.created_at || '').slice(0, 10);
            return (
              <div key={s.session_id} className="flex items-center justify-between bg-slate-50 rounded-xl p-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-[#ECFEFF] text-[#0E7490] flex items-center justify-center font-bold text-xs">#{s.session_number}</div>
                  <div>
                    <p className="font-semibold text-slate-700 text-sm">{s.child_name} — Session #{s.session_number}</p>
                    <p className="text-xs text-slate-400">{new Date(d + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={`rounded-full text-xs ${s.status === 'completed' ? 'bg-green-50 text-green-600 border-green-200' : 'bg-amber-50 text-amber-600 border-amber-200'}`}>{s.status}</Badge>
                  <Button size="sm" variant="ghost" className="text-slate-300 hover:text-red-500 h-7 w-7 p-0" onClick={() => setShowDeleteSession(s)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            );
          })}
          {filteredSessions.length === 0 && <p className="text-center text-slate-400 py-8">No sessions found</p>}
        </div>
      )}

      {view === 'hours' && (
        <div className="space-y-3">
          <div className="flex items-center gap-4 mb-2">
            <div className="bg-green-50 rounded-xl px-4 py-2 text-center">
              <p className="text-lg font-extrabold text-green-700">{filteredPay.hours}</p>
              <p className="text-xs text-green-600">Filtered Approved</p>
            </div>
            <div className="bg-[#ECFEFF] rounded-xl px-4 py-2 text-center">
              <p className="text-lg font-extrabold text-[#0E7490]">${filteredPay.total.toFixed(2)}</p>
              <p className="text-xs text-[#0E7490]">Filtered Pay</p>
            </div>
          </div>
          {filteredHours.map(h => (
            <Card key={h.log_id} className="rounded-2xl border-slate-100 shadow-sm" data-testid={`payroll-hour-${h.log_id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${h.type === 'session' ? 'bg-[#ECFEFF] text-[#0E7490]' : 'bg-amber-50 text-amber-600'}`}>
                    <Clock className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-700 text-sm">
                      {h.child_name ? `${h.child_name} — Session #${h.session_number}` : h.reason || `${h.type} hour`}
                    </p>
                    <p className="text-xs text-slate-400">
                      {h.session_date || new Date(h.created_at).toLocaleDateString()} · {h.hours}hr · ${(h.hours * (data.instructor.pay_rate || 37.5)).toFixed(2)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={`rounded-full text-xs ${h.status === 'approved' ? 'border-green-200 text-green-600 bg-green-50' : h.status === 'rejected' ? 'border-red-200 text-red-600 bg-red-50' : 'border-amber-200 text-amber-600 bg-amber-50'}`}>{h.status}</Badge>
                  {h.status === 'pending' && (
                    <div className="flex gap-1">
                      <Button size="sm" className="rounded-lg bg-green-500 hover:bg-green-600 text-white h-8 w-8 p-0" onClick={() => handleHourAction(h.log_id, 'approved')} data-testid={`approve-${h.log_id}`}><CheckCircle2 className="w-4 h-4" /></Button>
                      <Button size="sm" className="rounded-lg bg-red-400 hover:bg-red-500 text-white h-8 w-8 p-0" onClick={() => handleHourAction(h.log_id, 'rejected')} data-testid={`reject-${h.log_id}`}><XCircle className="w-4 h-4" /></Button>
                    </div>
                  )}
                  <Button size="sm" variant="ghost" className="text-slate-300 hover:text-red-500 h-8 w-8 p-0" onClick={() => deleteHour(h.log_id)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {filteredHours.length === 0 && <p className="text-center text-slate-400 py-8">No hours logged</p>}
        </div>
      )}

      {/* Delete Session Dialog */}
      <Dialog open={!!showDeleteSession} onOpenChange={() => setShowDeleteSession(null)}>
        <DialogContent className="rounded-3xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2" style={{ fontFamily: 'Nunito' }}><AlertTriangle className="w-5 h-5" /> Delete Session</DialogTitle>
            <DialogDescription className="sr-only">Confirm session deletion</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-slate-600">Delete <strong>{showDeleteSession?.child_name} — Session #{showDeleteSession?.session_number}</strong>? This removes all associated data.</p>
            <div className="grid grid-cols-2 gap-3">
              <Button variant="outline" className="rounded-xl font-bold" onClick={() => setShowDeleteSession(null)}>Cancel</Button>
              <Button className="rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold gap-2" onClick={() => deleteSession(showDeleteSession?.session_id)} data-testid="confirm-payroll-delete"><Trash2 className="w-4 h-4" /> Delete</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
